<template>
    <div :class="dense ? '' : 'pa-4 white rounded elevation-1'">
        <div class="d-flex align-center" :style="{ gap: dense ? '16px' : '32px' }">
            <!-- Totals -->
            <div class="flex-shrink-0" :style="{ minWidth: dense ? '100px' : '160px' }">
                <div class="d-flex align-baseline" style="gap: 4px;">
                    <span :class="dense ? 'text-h6' : 'text-h3'" class="font-weight-bold">{{ totalHours }}h</span>
                    <span :class="dense ? 'text-h6' : 'text-h3'" class="font-weight-bold">{{ totalMinutes }}m</span>
                </div>
                <div :class="dense ? 'text-caption' : 'text-body-2'" class="grey--text mb-1">Total Time Worked</div>
                <div class="d-flex" style="gap: 16px;">
                    <div>
                        <div :class="dense ? 'text-body-2' : 'text-h5'" class="font-weight-bold orange--text">{{ formatDuration(idleSeconds) }}</div>
                        <div class="text-caption text-uppercase grey--text">Idle Time</div>
                    </div>
                    <div v-if="showActive">
                        <div :class="dense ? 'text-body-2' : 'text-h5'" class="font-weight-bold green--text">{{ formatDuration(activeSeconds) }}</div>
                        <div class="text-caption text-uppercase grey--text">Active Time</div>
                    </div>
                    <div>
                        <div :class="dense ? 'text-body-2' : 'text-h5'" class="font-weight-bold primary--text">{{ daysActive }}</div>
                        <div class="text-caption text-uppercase grey--text">Days Active</div>
                    </div>
                </div>
            </div>

            <v-divider vertical />

            <!-- Day by day breakdown -->
            <div class="d-flex flex-grow-1" style="overflow-x: auto; min-width: 0;">
                <div v-for="day in dayRange" :key="day.date"
                    class="text-center flex-shrink-0"
                    :class="dense ? 'px-2 py-1' : 'px-3 py-2'"
                    :style="{ minWidth: dense ? '76px' : '140px', borderLeft: '1px solid #eeeeee' }">
                    <div v-if="dense" class="text-caption font-weight-bold mb-1">{{ day.weekdayShort }}, {{ day.dateLabel }}</div>
                    <template v-else>
                        <div class="text-caption font-weight-bold">{{ day.weekday }}</div>
                        <div class="text-caption font-weight-bold mb-3">{{ day.dateLabel }}</div>
                    </template>
                    <div class="grey lighten-2 rounded-pill" :style="{ height: dense ? '8px' : '12px', overflow: 'hidden' }">
                        <div class="green rounded-pill" :style="{ width: dayBarPct(day.seconds) + '%', height: '100%' }" />
                    </div>
                    <div class="text-caption mt-2" :class="day.seconds ? 'font-weight-medium' : 'grey--text'">
                        {{ formatDuration(day.seconds) }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DailyTimeBreakdown',

    props: {
        totalSeconds: { type: Number, default: 0 },
        idleSeconds: { type: Number, default: 0 },
        daysActive: { type: Number, default: 0 },
        // [{ date: 'YYYY-MM-DD', seconds: Number }, ...] — sparse, only days with tracked time.
        daily: { type: Array, default: () => [] },
        dateFrom: { type: String, required: true },
        dateTo: { type: String, required: true },
        // Compact sizing for embedding inline (e.g. in a list-row header) instead
        // of standing alone as its own card.
        dense: { type: Boolean, default: false },
        // Show an "Active Time" (total − idle) stat alongside Idle Time. Opt-in
        // so it only appears on the admin dashboard, not the personal one.
        showActive: { type: Boolean, default: false },
    },

    computed: {
        totalHours() {
            return Math.floor(this.totalSeconds / 3600);
        },
        activeSeconds() {
            return Math.max(0, this.totalSeconds - this.idleSeconds);
        },
        totalMinutes() {
            return Math.floor((this.totalSeconds % 3600) / 60);
        },
        // Every calendar day between dateFrom and dateTo (inclusive), filled with
        // seconds from `daily` where present and 0 otherwise — so days with no
        // tracked time still render as an empty card instead of being skipped.
        dayRange() {
            const secondsByDate = {};
            for (const d of this.daily) secondsByDate[d.date] = d.seconds;

            const days = [];
            const cur = new Date(this.dateFrom + 'T00:00:00');
            const end = new Date(this.dateTo + 'T00:00:00');
            // Safety cap so a bad date range can't spin the loop indefinitely.
            for (let i = 0; i < 366 && cur <= end; i++) {
                const iso = this.toDateStr(cur);
                const weekday = cur.toLocaleDateString('en-GB', { weekday: 'long' }) + ',';
                const weekdayShort = cur.toLocaleDateString('en-GB', { weekday: 'short' });
                const dateLabel = `${cur.toLocaleDateString('en-GB', { month: 'short' })} ${this.ordinalSuffix(cur.getDate())}`;
                days.push({ date: iso, weekday, weekdayShort, dateLabel, seconds: secondsByDate[iso] || 0 });
                cur.setDate(cur.getDate() + 1);
            }
            return days;
        },
    },

    methods: {
        toDateStr(d) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        },

        ordinalSuffix(n) {
            const j = n % 10, k = n % 100;
            if (j === 1 && k !== 11) return n + 'st';
            if (j === 2 && k !== 12) return n + 'nd';
            if (j === 3 && k !== 13) return n + 'rd';
            return n + 'th';
        },

        formatDuration(seconds) {
            if (!seconds) return '0h 0m';
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            return `${h}h ${m}m`;
        },

        // Bars are scaled against a full 24h day rather than the max day in
        // range, so the fill length stays visually comparable across ranges.
        dayBarPct(seconds) {
            return Math.min(100, Math.round((seconds / 86400) * 100));
        },
    },
};
</script>
